import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { WidgetsQuestionModel } from '../../../models/widgets/widgets-question.model';
import { QuestionEditorService } from 'src/app/services/widgets/question-editor.service';
import { getJsonPretty } from 'src/app/services/shared/getJsonPretty';
import { ToastrService } from 'ngx-toastr';
import { QuestionService } from 'src/app/services/api/question.service';
import { MetadataTemplateService } from 'src/app/services/metadata/metadata-template.service';
import { Config } from 'src/app/config';

export enum ConditionalTypeEnum {
  Manual,
  HideForOption,
  ShowForOption
}

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'appMetadataTemplate',
  templateUrl: './metadata-template.component.html',
  styleUrls: ['./metadata-template.component.scss'],
})

export class MetadataTemplateComponent implements OnInit, OnDestroy, OnChanges, AfterViewInit {
  @Input() primitive;
  @Input() template;
  @Input() question;
  @Input() isQuestionSelected?: boolean;
  @Output() modifiedJsonTemplate: EventEmitter<any> = new EventEmitter();

  prettyJsonTemplate;
  relatedPrimitives: string[] = [];
  currentQuestion?: WidgetsQuestionModel = new WidgetsQuestionModel();
  conditionalType?: string;
  conditionalTypeEnum?: ConditionalTypeEnum = ConditionalTypeEnum.Manual;
  selectedRelatedPrimitive?: string;
  selectedPrimitiveOption?: string;
  primitiveOptions: any[] = [];
  valueOfPrimitiveOption?: string;
  primitiveType?: string;
  primitiveLabel?: string;

  constructor(
    private toaster: ToastrService, 
    private qeService: QuestionEditorService, 
    private questionService: QuestionService,
    private templateService: MetadataTemplateService) {
  }

  ngOnInit(): void {
    this.conditionalType = "Manual";
    if (this.isQuestionSelected && this.question.template) {
      const parsedQuestionTemplate = JSON.parse(this.question.template);
      this.prettyJsonTemplate = getJsonPretty(parsedQuestionTemplate);
    } else if (!this.isQuestionSelected && this.primitive.template) {
      const parsedPrimitiveTemplate = JSON.parse(this.primitive.template);
      this.prettyJsonTemplate = getJsonPretty(parsedPrimitiveTemplate);
      this.conditionalType = this.primitive.conditionalType;
      this.selectedRelatedPrimitive = this.primitive.relatedPrimitive;
      this.selectedPrimitiveOption = this.primitive.primitiveOption;
    }
    if (!this.isQuestionSelected) {
      this.getCurrentQuestion();
    }
  }

  ngAfterViewInit() {
    if (!this.isQuestionSelected) {
      if (this.primitive.conditionalType) {
        this.conditionalType = this.primitive.conditionalType;
      }
      this.getAllRelatedPrimitives();
      if (this.primitive.relatedPrimitive && this.primitive.primitiveOption) {
        this.selectedRelatedPrimitive = this.primitive.relatedPrimitive;
        this.getAllPrimitiveOptions(this.selectedRelatedPrimitive)
        this.selectedPrimitiveOption = this.primitive.primitiveOption;
      }
    }
  }

  ngOnChanges() {
    // Update template field
    if (this.isQuestionSelected && this.question.template) {
      const parsedQuestionTemplate = JSON.parse(this.question.template);
      this.prettyJsonTemplate = getJsonPretty(parsedQuestionTemplate);
    } else if (!this.isQuestionSelected && this.primitive.template) {
      const parsedPrimitiveTemplate = JSON.parse(this.primitive.template);
      this.prettyJsonTemplate = getJsonPretty(parsedPrimitiveTemplate);
    }
  } 

  getUserValue(data: any) {
    this.validateJSON(data)
  }

  /**
   * 
   * @param data Receives a string that represent a JSON
   * @returns 
   */
  validateJSON(data: string) {
    if (data) {
      try {
        const parsedJson = JSON.parse(data);
        if (parsedJson && typeof parsedJson === "object") {
          this.modifiedJsonTemplate.emit({ template: data });
          this.toaster.success('Template successfully updated.');
        }
        // else {
        //   this.toaster.error('Invalid JSON format');
        // }
      } catch (e) {
        this.toaster.error('Invalid JSON format');
        return false;
      }
    }
    return true;
  }

  getCurrentQuestion() {
    this.currentQuestion = this.qeService.getQuestionRoot();
  }

  getConditionalData(val: any) {
    if (this.conditionalType === 'Hide based on one option') {
      this.getAllRelatedPrimitives();
      this.conditionalTypeEnum = ConditionalTypeEnum.HideForOption;
    } else if (this.conditionalType === 'Show based on one option') {
      this.getAllRelatedPrimitives();
      this.conditionalTypeEnum = ConditionalTypeEnum.ShowForOption;
    } else {
      this.primitive.template = "";
      this.selectedRelatedPrimitive = "";
      this.selectedPrimitiveOption = "";
      this.prettyJsonTemplate = "";
    }
  }

  ngOnDestroy() {
    if (!this.isQuestionSelected) {
      if (this.primitive.conditionalType && this.primitive.relatedPrimitive && this.primitive.primitiveOption) {
        this.primitive.conditionalType = this.conditionalType;
        this.primitive.relatedPrimitive = this.selectedRelatedPrimitive;
        this.primitive.primitiveOption = this.selectedPrimitiveOption;
      }
    }
  }

  getAllRelatedPrimitives() {
    this.relatedPrimitives = [];
    this.currentQuestion?.children.forEach((index) => {
      if (index['key'] !== this.primitive.key) {
        this.relatedPrimitives.push(index['key'])
      }
    });
  }

  getAllPrimitiveOptions(val: any) {
    this.primitiveOptions = [];
    this.currentQuestion?.children.forEach((index) => {
      if (index['key'] === this.selectedRelatedPrimitive) {
        if (index['type'] === "radio" || index['type'] === "checkbox") {
          let options: any[] = [];
          options = index['options'];
          options.forEach((res) => {
            if (res.key) {
              this.primitiveOptions?.push({ label: res.key, value: res.value, type: index['type'] });
            } else {
              this.primitiveOptions?.push({ label: res.label, value: res.value, type: index['type'] });
            }
          });
          // Add "Other" option if present
          if (index['otherFlag'] === true) {
            const isExtended = index['isExtended'];
            let otherValue = Config.CONSTANTS.otherKeySuffix;
            if (isExtended !== undefined && isExtended === true) {
                otherValue = index['key'] + Config.CONSTANTS.otherKeySuffix;
            }
            const otherLabel = index['otherLabel'] ? index['otherLabel'] : Config.CONSTANTS.otherKeySuffix;
            this.primitiveOptions?.push({ label: otherLabel, value: otherValue, type: index['type'] });
          }
        } else if (index['type'] === "input") {
          this.primitiveOptions = [{ label: 'Empty', value: "===''", type: 'input' }, { label: 'Populated', value: "==''", type: 'input' }];
        }
      }
    });
  }

  getNewTemplate(val: any) {
    this.valueOfPrimitiveOption = "";
    this.primitiveOptions?.forEach((index) => {
      if (index.label === this.selectedPrimitiveOption) {
        this.valueOfPrimitiveOption = index.value;
        this.primitiveType = index.type;
        this.primitiveLabel = index.label;
      }
    });
    let data = this.templateService.createNewTemplate(this.conditionalTypeEnum!, this.valueOfPrimitiveOption, this.selectedRelatedPrimitive, this.primitiveType, this.primitiveLabel);
    if (!this.isQuestionSelected) {
      this.modifiedJsonTemplate.emit({ template: data, conditionalType: this.conditionalType, relatedPrimitive: this.selectedRelatedPrimitive, primitiveOption: this.selectedPrimitiveOption });
    } else {
      this.modifiedJsonTemplate.emit({ template: data });
    }
  }
}